import React, { useEffect, useState } from "react";
import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useTheme from "@material-ui/core/styles/useTheme";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { useRacehorse360Api } from "hooks/api";
import Accordion from "components/Accordion";
import Loader from "components/Loader";
import MediaPlayButton from "components/MediaPlayButton";
import Pagination from "components/Pagination";
import { TabTable } from "components/TabTables/TabTables";
import WorkoutDetails from "./WorkoutDetails";
import PaperCarousel from "../../PaperCarousel";
import { SortOrder } from "interfaces/SortOrder";

import useStyles from "./styles";

export interface Props extends TabTable {
  horse: racehorse360.IHorse;
  pagination?: boolean;
  rootRef: React.RefObject<HTMLElement>;
  onLoad?: (workouts, pagingInfo) => void;
  isSliderOnMobile?: boolean;
}

const PAGE_SIZE = 7;
const SLIDE_SIZE = 5;

type QueryResponse = {
  isLoading: boolean;
  data: racehorse360.IListWorkoutsResponse;
};

const Workouts = (props: Props) => {
  const { horse, pagination, rootRef, onLoad, isSliderOnMobile } = props;

  const classes = useStyles();
  const theme = useTheme();
  const maxLengthHelperText: number = 455;
  const isSliderView = !pagination && isSliderOnMobile;
  const matchesUpMd = useMediaQuery(theme.breakpoints.up("md"));

  const [activePage, setActivePage] = useState<number>(1);
  const [showModalFlag, setShowModalFlag] = useState<boolean>(false);
  const [commentText, setCommentText] = useState<string>("");
  const [selectedWorkout, setSelectedWorkout] = useState<string>("");
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [workoutsSlides, setWorkoutsSlides] = useState<
    racehorse360.IWorkout[][]
  >([]);

  const { useListWorkouts } = useRacehorse360Api();

  const { isLoading, data = {} }: QueryResponse = useListWorkouts(
    {
      query: {
        horseIds: [horse.id]
      },
      pagingOptions: {
        maxResults: isSliderView ? SLIDE_SIZE : PAGE_SIZE,
        offset: isSliderView
          ? activeSlide * SLIDE_SIZE
          : (activePage - 1) * PAGE_SIZE,
        includeSummary: true
      },
      getOptions: {
        select: [
          "id",
          "date",
          "facility.code",
          "comment",
          "courseSurface",
          "distance",
          "distanceUnit",
          "workoutType",
          "timing",
          "ranking",
          "numberInRankingGroup",
          "trackCondition",
          "gateIndicator",
          "dogIndicator"
        ],
        orderBy: [`date ${SortOrder.DESC}`]
      }
    },
    {
      onSuccess: ({ workouts, pagingInfo }) => {
        if (isSliderView) {
          const slides = isInitialLoad
            ? new Array(Math.ceil(pagingInfo.totalResults / SLIDE_SIZE)).fill(
                null
              )
            : [...workoutsSlides];

          slides[activeSlide] = workouts;

          setWorkoutsSlides(slides);
          setIsInitialLoad(false);
        }
        onLoad && onLoad(workouts, pagingInfo);
      },
      onError: error => console.error(error)
    }
  );

  const { workouts = [], pagingInfo: { totalResults = 0 } = {} } = data || {};

  const from = pagination ? 0 : activeSlide * SLIDE_SIZE + 1;
  const to =
    !pagination && activeSlide * SLIDE_SIZE + SLIDE_SIZE < totalResults
      ? activeSlide * SLIDE_SIZE + SLIDE_SIZE
      : totalResults;

  const handleCancelClick = (): void => {
    setShowModalFlag(false);
    setCommentText("");
    setSelectedWorkout("");
  };

  const handleSaveClick = (): void => {
    setShowModalFlag(false);
    setCommentText("");
    setSelectedWorkout("");
  };

  const handleInputChange = (event): void => {
    const target = event.target as HTMLInputElement;

    setCommentText(target.value);
  };

  const handleOpenVideo = () => {
    return;
  };

  const handleSlideChange = index => {
    setActiveSlide(index);
  };

  useEffect(() => {
    if (data?.workouts && onLoad) {
      onLoad(data?.workouts, data?.pagingInfo);
    }
  }, []);

  const renderWorkouts = (items = null) => {
    const sliced = isSliderView ? items : workouts;

    if (!sliced || (!isSliderView && isLoading)) {
      return <Loader className={classes.loader} />;
    }

    if (workouts.length || isLoading) {
      return (
        <Box className={classes.workoutList}>
          <Box className={clsx(classes.workoutRow, classes.workoutRowHeader)}>
            <Box className={classes.workoutDate}>Date</Box>
            <Box className={classes.workoutTrack}>Track</Box>
            <Box className={classes.workoutDetailsData}>Workout Details</Box>

            {/*ALERT! After return field check styles-workoutRow-gridTemplateColumns*/}
            <Box className={classes.workoutReplay} />

            {/*TODO: return "Analysis" after MVP*/}
            {/*ALERT! After return field check styles-workoutRow-gridTemplateColumns*/}
            {/*<Box className={classes.workoutAnalysisHead}></Box>*/}
          </Box>

          {sliced?.map(workout => (
            <Box key={workout.id} className={classes.workout}>
              <Box
                className={clsx(classes.workoutRow, {
                  [classes.workoutRowSelected]: selectedWorkout === workout.id
                })}
              >
                <Box className={classes.workoutDateCell}>
                  {format(parseISO(workout.date), "MM/dd/yy")}
                </Box>
                <Box className={classes.workoutTrack}>
                  {workout.facility?.code}
                </Box>
                <Box className={classes.workoutDetailsData}>
                  <WorkoutDetails workout={workout} />
                </Box>

                <Box className={clsx(classes.workoutReplay, classes.hidden)}>
                  <MediaPlayButton
                    onOpenMedia={handleOpenVideo}
                    isDisabled={!workout["replay"]}
                  />
                </Box>

                {/*TODO: return "Analysis" after MVP*/}
                {/*ALERT! After return field check styles-workoutRow-gridTemplateColumns*/}
                <Box className={classes.workoutAnalysisHead}>
                  {/*  <IconButton //TODO: hidden for MVP*/}
                  {/*    size="small"*/}
                  {/*    onClick={() => handleAnalysisClick(workout.id)}*/}
                  {/*    disabled={false}*/}
                  {/*  >*/}
                  {/*    <AddCircleOutlineIcon*/}
                  {/*      className={classes.addCircleOutlineIcon}*/}
                  {/*    />*/}
                  {/*  </IconButton>*/}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    return <div className={classes.emptyDataHolder}>No Workouts</div>;
  };

  if (isLoading && pagination) {
    return <Loader className={classes.loader} />;
  }

  return (
    <>
      {pagination && matchesUpMd ? (
        <Box className={classes.tableWrapper}>
          {renderWorkouts()}
          {!showModalFlag && workouts?.length > 0 && (
            <Pagination
              pageNumber={activePage}
              totalCount={totalResults}
              pageSize={PAGE_SIZE}
              onPageChange={setActivePage}
            />
          )}
          {showModalFlag && (
            <Box className={classes.modal}>
              <Box className={classes.modalMessage}>
                <TextField
                  className={clsx(classes.textField, {
                    [classes.textFieldError]:
                      commentText.length > maxLengthHelperText
                  })}
                  label="Flagged Workout Comment"
                  rows={8}
                  fullWidth
                  onChange={handleInputChange}
                  multiline
                  helperText={`${commentText.length} / ${maxLengthHelperText}`}
                  variant="outlined"
                  value={commentText || ""}
                  error={commentText.length > maxLengthHelperText}
                />
              </Box>
              <Box className={classes.modalButtons}>
                <Button onClick={handleCancelClick} size="large">
                  Cancel
                </Button>
                <Button
                  className={classes.saveButton}
                  onClick={handleSaveClick}
                  size="large"
                  disabled={commentText.length > maxLengthHelperText}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Accordion
          disabled={(!workouts?.length && !isLoading) || isLoading}
          className={classes.accordion}
          count={totalResults}
          name={"Workouts"}
          rootRef={rootRef}
          showCountLoader={isSliderView}
          hideBackground
          hideShadow
          roundCount
          isLoading={isLoading}
          hideSubtitleWhenCollapsed
          subtitle={
            workouts.length ? (
              `Showing ${from}-${to} of ${totalResults}`
            ) : (
              <Loader size={16} />
            )
          }
        >
          <PaperCarousel
            items={workoutsSlides}
            itemRenderer={renderWorkouts}
            onSlideChange={handleSlideChange}
          />
        </Accordion>
      )}
    </>
  );
};

export default React.memo(Workouts);
